import {
  Check,
  Copy01,
  RefreshCw01,
  ThumbsDown,
  ThumbsUp,
} from '@untitledui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { FeedbackRating } from '@/lib/reasoning/feedback';
import { copyToClipboard } from '@/lib/utils/copyToClipboard';
import { cx } from '@/utils/cx';

import { RETRYING_TOOLTIP } from './ChainNotices';
import { ShowReasoningButton } from './ShowReasoningButton';

/** How long the copy button shows its "Copied" confirmation. */
const COPIED_FEEDBACK_MS = 2000;

interface ChainActionBarProps {
  chainId: string;
  /** Content the copy button copies — the final answer's markdown. */
  content: string;
  hasSteps: boolean;
  /** Retry is offered only on the most recent completed chain. */
  canRetry: boolean;
  isRetryDisabled: boolean;
  isRegenerating: boolean;
  feedback: FeedbackRating | null | undefined;
  onRetry: (chainId: string) => void;
  onFeedback: (chainId: string, rating: FeedbackRating) => void;
  onOpenReasoningSidebar: (chainId: string) => void;
}

/** Row under a final answer: show reasoning, retry, copy, and feedback. */
export function ChainActionBar({
  chainId,
  content,
  hasSteps,
  canRetry,
  isRetryDisabled,
  isRegenerating,
  feedback,
  onRetry,
  onFeedback,
  onOpenReasoningSidebar,
}: ChainActionBarProps) {
  const { isCopied, copy } = useCopiedFeedback();

  const handleRetry = useCallback(() => onRetry(chainId), [onRetry, chainId]);
  const handleCopy = useCallback(() => copy(content), [copy, content]);
  const handlePositive = useCallback(
    () => onFeedback(chainId, 'positive'),
    [onFeedback, chainId]
  );
  const handleNegative = useCallback(
    () => onFeedback(chainId, 'negative'),
    [onFeedback, chainId]
  );

  return (
    <div className='flex items-center justify-between pt-1'>
      {hasSteps && (
        <ShowReasoningButton
          chainId={chainId}
          onOpenReasoningSidebar={onOpenReasoningSidebar}
        />
      )}

      <div className='flex items-center gap-1.5 ml-auto'>
        {/* TODO(copy): Laura sign-off — retry framing for AI content (no "regenerate"). */}
        {canRetry && (
          <Button
            color='tertiary'
            onClick={handleRetry}
            isDisabled={isRetryDisabled}
            disabledTooltip={RETRYING_TOOLTIP}
            title='Try again'
            aria-label='Try again'
            className='!p-1.5 !rounded'
            iconLeading={
              <RefreshCw01
                className={cx(
                  'size-4 stroke-tertiary',
                  isRegenerating && 'animate-spin'
                )}
                strokeWidth={2}
              />
            }
          />
        )}
        <Button
          color='tertiary'
          onClick={handleCopy}
          title={isCopied ? 'Copied' : 'Copy'}
          className={cx('!p-1.5 !rounded', isCopied && 'bg-bg-secondary')}
          iconLeading={
            isCopied ? (
              <Check
                className='size-4 stroke-utility-success-600'
                strokeWidth={2}
              />
            ) : (
              <Copy01 className='size-4 stroke-tertiary' strokeWidth={2} />
            )
          }
        />
        <Button
          color='tertiary'
          onClick={handlePositive}
          title='Thumbs up'
          className={cx(
            '!p-1.5 !rounded',
            feedback === 'positive' && 'bg-bg-secondary'
          )}
          iconLeading={
            <ThumbsUp
              className={cx(
                'size-4',
                feedback === 'positive'
                  ? 'stroke-utility-success-600 fill-utility-success-100'
                  : 'stroke-tertiary'
              )}
              strokeWidth={2}
            />
          }
        />
        <Button
          color='tertiary'
          onClick={handleNegative}
          title='Thumbs down'
          className={cx(
            '!p-1.5 !rounded',
            feedback === 'negative' && 'bg-bg-secondary'
          )}
          iconLeading={
            <ThumbsDown
              className={cx(
                'size-4',
                feedback === 'negative'
                  ? 'stroke-utility-error-600 fill-utility-error-100'
                  : 'stroke-tertiary'
              )}
              strokeWidth={2}
            />
          }
        />
      </div>
    </div>
  );
}

/**
 * Copy-to-clipboard with a transient "copied" flag. The flag is local to one
 * action bar — no other part of the chat reads it — and its timer is cleared
 * on unmount so it never sets state on a removed row.
 */
function useCopiedFeedback() {
  const [value, setValue] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const copy = useCallback(async (content: string) => {
    if (!content) {
      return;
    }
    const success = await copyToClipboard(content);
    if (!success) {
      return;
    }
    setValue(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setValue(false), COPIED_FEEDBACK_MS);
  }, []);

  return { isCopied: value, copy };
}
